import React from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
  MinusIcon,
  PresentationChartBarIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TruncatedText from 'components/TruncatedText';
import vistaService from 'services/vistaService';
import { Submission } from 'types/evaluation';
import { INDIVIDUAL_CATEGORIES } from 'constants/evaluation';
import { processAIJudgmentForSubmission } from 'services/vistaJudging';
import { useRoleInfo } from 'hooks/useRoleInfo';
import { useAuth } from 'contexts/AuthContext';

export interface StageActionLog {
  id: string;
  actor_name: string;
  actor_email: string;
  action: 'advance' | 'revert';
  submission_id: string;
  submission_name: string;
  submission_pillar: string;
  submission_category: string;
  previous_stage: string;
  new_stage: string;
  timestamp: string;
}

const SubmissionRow = ({
  index,
  submission,
  expanded,
  toggleRowExpansion,
  averageRank,
  showRanking = false,
  showSubmitterName = false,
  showStageAction = false,
  showActionMaterial = true,
  fromPage,
  onStageChange,
  showSubmissionDate = false,
  onRecalculateComplete,
  showCategory = false,
}: {
  index: number;
  submission: Submission;
  expanded: boolean;
  toggleRowExpansion: (id: string | number) => void;
  averageRank?: number | null;
  showRanking?: boolean;
  showSubmitterName?: boolean;
  showStageAction?: boolean;
  showActionMaterial?: boolean;
  fromPage?: 'preselectionToQualification' | 'qualificationToFinal';
  onStageChange?: (
    documentId: string,
    newStage: string,
    updatedLogs?: StageActionLog[]
  ) => void;
  showSubmissionDate?: boolean;
  onRecalculateComplete?: () => void;
  showCategory?: boolean;
}) => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { userIsSuperAdmin } = useRoleInfo();
  const { profile } = useAuth();
  const [isSaving, setIsSaving] = React.useState(false);
  const [isRecalculating, setIsRecalculating] = React.useState(false);

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      // 1. Run AI Judgment directly since submission already has values
      let aiRecaptchaToken: string;
      if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true') {
        aiRecaptchaToken = 'dev-bypass';
      } else {
        if (!executeRecaptcha) {
          throw new Error('reCAPTCHA not ready');
        }
        aiRecaptchaToken = await executeRecaptcha('submit_award_ai');
      }

      const judgmentResult = await processAIJudgmentForSubmission(
        submission.documentId,
        {},
        aiRecaptchaToken
      );

      if (!judgmentResult.success) {
        throw new Error(
          judgmentResult.error || 'AI judgment generation failed'
        );
      }

      alert('Recalculation successful!');
      onRecalculateComplete?.();
    } catch (err: any) {
      console.error('Recalculate error:', err);
      alert(`Recalculation failed: ${err.message || String(err)}`);
    } finally {
      setIsRecalculating(false);
    }
  };

  // Determine what to display in the Project/Profile Name column
  const getDisplayName = () => {
    const isIndividualCategory = INDIVIDUAL_CATEGORIES.includes(
      submission.category
    );

    if (isIndividualCategory) {
      // For individual categories, show the full name instead of description
      return submission.full_name || submission.description || 'No Name';
    } else {
      // For organization categories, show the project name from description field
      return (
        submission.description ||
        submission.project_name ||
        submission.title ||
        'No Project Name'
      );
    }
  };

  const displayName = showSubmitterName
    ? submission.full_name || 'No Name'
    : getDisplayName();

  let stageLabel: string;
  switch (submission.competition_stage) {
    case 'final':
      stageLabel = 'Final';
      break;
    case 'qualification':
      stageLabel = 'Qualification';
      break;
    default:
      stageLabel = 'PreSelection';
  }

  let canToggleStage: boolean;
  switch (fromPage) {
    case 'qualificationToFinal':
      canToggleStage =
        submission.competition_stage === 'qualification' ||
        submission.competition_stage === 'final';
      break;
    default:
      canToggleStage =
        submission.competition_stage === '' ||
        submission.competition_stage === null ||
        submission.competition_stage === 'qualification';
  }

  const handleStageToggle = async () => {
    let newStage: string;
    switch (fromPage) {
      case 'qualificationToFinal':
        newStage =
          submission.competition_stage === 'final' ? 'qualification' : 'final';
        break;
      default:
        newStage =
          submission.competition_stage === 'qualification'
            ? ''
            : 'qualification';
    }

    // Determine action label for logging
    const isAdvancing =
      fromPage === 'qualificationToFinal'
        ? submission.competition_stage !== 'final'
        : submission.competition_stage !== 'qualification';
    const actionLabel = isAdvancing ? 'advance' : 'revert';
    const previousStage = submission.competition_stage || 'preselection';

    setIsSaving(true);
    try {
      let recaptchaToken: string | undefined;
      if (
        import.meta.env.VITE_DISABLE_RECAPTCHA !== 'true' &&
        executeRecaptcha
      ) {
        recaptchaToken = await executeRecaptcha('stage_toggle');
      }

      // --- Build log entry before the PUT ---
      const logPayload: StageActionLog = {
        id: `${Date.now()}-${submission.documentId}`,
        actor_name: profile?.fullName || 'Unknown',
        actor_email: profile?.email || 'Unknown',
        action: actionLabel as 'advance' | 'revert',
        submission_id: submission.documentId,
        submission_name:
          submission.full_name ||
          submission.description ||
          submission.title ||
          'Unknown',
        submission_pillar: submission.pillar || 'Unknown',
        submission_category: submission.category || 'Unknown',
        previous_stage: previousStage,
        new_stage: newStage || 'preselection',
        timestamp: new Date().toISOString(),
      };

      const updatedLogs: StageActionLog[] = [
        ...(submission.stage_action_logs || []),
        logPayload,
      ];

      // Single PUT: persist stage change + append log entry
      await vistaService.updateAwardSubmission(submission.documentId, {
        competition_stage: newStage,
        stage_action_logs: updatedLogs,
        recaptchaToken: recaptchaToken || 'dev-bypass',
      });

      onStageChange?.(submission.documentId, newStage, updatedLogs);

      console.log(
        `[Stage Action] ${logPayload.actor_name} (${logPayload.actor_email}) ${actionLabel.toUpperCase()}D "${logPayload.submission_name}" from ${logPayload.previous_stage} → ${logPayload.new_stage}`,
        logPayload
      );
      // --- End Audit Log ---
    } catch {
      alert('Failed to update stage. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  let stageToggleLabel: React.ReactNode;
  switch (fromPage) {
    case 'qualificationToFinal':
      stageToggleLabel =
        submission.competition_stage === 'final' ? (
          <span className="flex items-center gap-1">&#8592; Revert</span>
        ) : (
          <span className="flex items-center gap-1">Advance &#8594;</span>
        );
      break;
    default:
      stageToggleLabel =
        submission.competition_stage === 'qualification' ? (
          <span className="flex items-center gap-1">&#8592; Revert</span>
        ) : (
          <span className="flex items-center gap-1">Advance &#8594;</span>
        );
  }

  let showMaterialUpdateBtn: boolean;
  switch (fromPage) {
    case 'preselectionToQualification':
    case 'qualificationToFinal':
      showMaterialUpdateBtn =
        submission.competition_stage === 'qualification' ||
        submission.competition_stage === 'final';
      break;
    default:
      showMaterialUpdateBtn = false;
  }

  return (
    <tr
      className={`hover:bg-blue-50 transition-colors duration-200 border-b border-gray-100 ${
        expanded ? 'bg-blue-50 shadow-sm' : 'bg-white'
      }`}
    >
      <td className="py-4 px-2 align-middle">
        <button
          onClick={() => toggleRowExpansion(submission.id)}
          className={`p-2 flex flex-row items-center gap-1 hover:bg-blue-100 rounded-lg transition-colors ${expanded ? 'bg-blue-100' : ''}`}
        >
          {index + 1}.
          {expanded ? (
            <ChevronDownIcon className="w-4 h-4 text-blue-600" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </td>
      <td className="py-4 px-2 align-middle border-r border-gray-100">
        <div className="min-w-0">
          <TruncatedText
            text={displayName}
            className={showSubmitterName ? 'font-bold' : ''}
            maxLines={3}
          />
        </div>
      </td>
      <td className="py-4 px-4 align-middle border-r border-gray-100 max-w-[200px]">
        <TruncatedText
          text={submission.email}
          maxLines={1}
          className="text-gray-600"
        />
      </td>
      <td className="py-4 px-4 align-middle border-r border-gray-100 max-w-[150px]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
          <TruncatedText
            text={
              submission.pillar === 'Others' ? 'Initiative' : submission.pillar
            }
            maxLines={1}
            className="text-gray-700"
          />
        </div>
      </td>
      {showCategory && (
        <td className="py-4 px-4 align-middle border-r border-gray-100 max-w-[180px]">
          <TruncatedText
            text={submission.category}
            maxLines={1}
            className="text-gray-700"
          />
        </td>
      )}
      <td className="py-4 px-4 align-middle border-r border-gray-100 max-w-[200px]">
        <TruncatedText
          text={submission.organization_unit}
          maxLines={1}
          className="text-gray-600"
        />
      </td>
      {showSubmissionDate && (
        <td className="py-4 px-4 align-middle border-r border-gray-100 max-w-[150px]">
          <span className="text-gray-600 text-xs font-baijamjuree font-medium block">
            {submission.submitted_date
              ? new Date(submission.submitted_date).toLocaleString('en-GB', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '-'}
          </span>
        </td>
      )}
      {showRanking && (
        <td className="py-4 px-4 text-center align-top border-r border-gray-100">
          <div className=" items-start justify-center pt-1">
            {averageRank !== null && averageRank !== undefined ? (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2">
                <span className="text-base font-baijamjuree font-extrabold">
                  {averageRank.toFixed(2)}
                </span>
              </div>
            ) : (
              <span className="text-gray-400 text-sm font-baijamjuree bg-gray-100 rounded-lg px-3 py-2">
                N/A
              </span>
            )}
          </div>
        </td>
      )}
      <td className="py-4 px-4 text-center align-top">
        <div className="flex items-start justify-center pt-1">
          {submission.total_score !== null &&
          submission.total_score !== undefined ? (
            <div className="bg-orange-50 border border-orange-200 text-orange-700 rounded-lg px-2 py-1">
              <span className="text-xs font-baijamjuree font-semibold">
                {submission.total_score.toFixed(2)}
              </span>
              <span className="text-[10px] text-orange-500 ml-0.5">/10</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5 pt-1">
              <span className="text-gray-400 text-xs font-baijamjuree bg-gray-100 rounded-lg px-2 py-1">
                Pending
              </span>
              {fromPage === 'preselectionToQualification' &&
                userIsSuperAdmin && (
                  <button
                    onClick={handleRecalculate}
                    disabled={isRecalculating}
                    className={`p-1.5 rounded-lg border transition-all duration-200 shadow-sm ${
                      isRecalculating
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:shadow active:scale-95'
                    }`}
                    title="Recalculate AI Score"
                  >
                    {isRecalculating ? (
                      <div className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                      </svg>
                    )}
                  </button>
                )}
            </div>
          )}
        </div>
      </td>
      {showStageAction && (
        <td className="py-4 px-4 text-center align-top border-l border-r border-gray-100">
          <div className="flex items-start justify-center pt-1">
            <span className="font-baijamjuree text-xs text-gray-700 px-3 py-2">
              {stageLabel}
            </span>
          </div>
        </td>
      )}
      {showActionMaterial && (
        <>
          <td className="py-4 px-2 text-center align-top">
            {canToggleStage ? (
              <div className="flex items-start justify-center pt-1">
                <button
                  onClick={handleStageToggle}
                  disabled={isSaving}
                  title="Click to change Stage"
                  className="rounded-lg px-3 py-2 border font-baijamjuree text-[10px] font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                >
                  {isSaving ? (
                    <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mx-auto" />
                  ) : (
                    stageToggleLabel
                  )}
                </button>
              </div>
            ) : null}
          </td>
          {showMaterialUpdateBtn ? (
            <td className="py-4 px-2 text-center align-top border-l border-gray-100">
              <div className="flex flex-col items-center gap-1 pt-1">
                <button
                  onClick={() =>
                    navigate('/form', { state: { editSubmission: submission } })
                  }
                  disabled={isSaving}
                  title="Submit Qualification Material"
                  className="rounded-lg px-3 py-2 border font-baijamjuree text-[10px] font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                >
                  Update
                </button>
                <div className="flex items-center gap-2">
                  <div
                    title={
                      submission.detailed_fields?.['Award Material']
                        ? 'Presentation uploaded'
                        : 'No presentation'
                    }
                    className="flex items-center gap-0.5"
                  >
                    <PresentationChartBarIcon className="w-3 h-3 text-gray-400" />
                    {submission.detailed_fields?.['Award Material'] ? (
                      <CheckIcon className="w-2.5 h-2.5 text-green-500 stroke-[3]" />
                    ) : (
                      <MinusIcon className="w-2.5 h-2.5 text-gray-300 stroke-[3]" />
                    )}
                  </div>
                  <div
                    title={
                      submission.detailed_fields?.['Video URL']
                        ? 'Video uploaded'
                        : 'No video'
                    }
                    className="flex items-center gap-0.5"
                  >
                    <VideoCameraIcon className="w-3 h-3 text-gray-400" />
                    {submission.detailed_fields?.['Video URL'] ? (
                      <CheckIcon className="w-2.5 h-2.5 text-green-500 stroke-[3]" />
                    ) : (
                      <MinusIcon className="w-2.5 h-2.5 text-gray-300 stroke-[3]" />
                    )}
                  </div>
                </div>
              </div>
            </td>
          ) : (
            <td className="border-l border-gray-100"></td>
          )}
        </>
      )}
    </tr>
  );
};

export default SubmissionRow;
